import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import MuiDialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import {
  AMBIENT_SCRIBE_CUSTOM_TEMPLATES,
  AMBIENT_SCRIBE_TEMPLATE_TYPES
} from "constants/feature-flags";
import AmbientScribeTemplateSelect, {
  AMBIENT_SCRIBE_TEMPLATES_QUERY_PARAMS,
  BUILT_IN_CHART_NOTE_TEMPLATE_LABEL
} from "features/medical-scribe/components/ambient-scribe-template-select.component";
import { isChartNoteTemplate } from "features/medical-scribe/components/scribe-template-type.util";
import useScribeTemplateAppointmentTypes from "features/medical-scribe/components/use-scribe-template-appointment-types.hook";
import { getScribeModalNestedDialogZIndex } from "features/medical-scribe/medical-scribe-constants";
import {
  AMBIENT_SCRIBE_TEMPLATE_SELECT_QUERY,
  useGenerateAmbientScribeAsyncMutation,
  useGetAmbientScribeTemplatesQuery
} from "services/ambient-scribe";
import type { AmbientScribeTemplateType } from "services/types/ambient-scribe-template.type";

type GenerateError =
  | { kind: "subscription"; error: string; message: string }
  | { kind: "generic" };

type GenerateScribeSectionProps = {
  scribeId: number;
  isGeneratingSoap: boolean | null | undefined;
  scribeTemplateId?: number | null;
  /** Restricts the picker to one template type. Unset → no `?type=` filter (all types). */
  templateType?: AmbientScribeTemplateType;
  onGenerateStarted: () => void;
};

export default function GenerateScribeSection({
  scribeId,
  isGeneratingSoap,
  scribeTemplateId,
  templateType,
  onGenerateStarted
}: Readonly<GenerateScribeSectionProps>) {
  const theme = useTheme();
  const flags = useFlags();
  const isAmbientScribeCustomTemplatesEnabled =
    flags[AMBIENT_SCRIBE_CUSTOM_TEMPLATES];
  const isTemplateTypesEnabled = Boolean(flags[AMBIENT_SCRIBE_TEMPLATE_TYPES]);
  // Three states, not two. `null` means the user picked the built-in prompt — a real choice — so
  // `undefined` is what carries "untouched". See `AmbientScribeTemplateSelect`'s `value` contract.
  const [selectedTemplateId, setSelectedTemplateId] = useState<
    number | null | undefined
  >(undefined);

  const [generateError, setGenerateError] = useState<GenerateError | null>(
    null
  );

  // Flag off → the exported constant verbatim, so the request URL is unchanged.
  const templatesQueryParams = useMemo(() => {
    if (!isTemplateTypesEnabled) return AMBIENT_SCRIBE_TEMPLATES_QUERY_PARAMS;
    return {
      ...AMBIENT_SCRIBE_TEMPLATES_QUERY_PARAMS,
      query: AMBIENT_SCRIBE_TEMPLATE_SELECT_QUERY,
      ...(templateType ? { type: templateType } : {})
    };
  }, [isTemplateTypesEnabled, templateType]);

  const { data: templates = [] } = useGetAmbientScribeTemplatesQuery(
    templatesQueryParams,
    { refetchOnMountOrArgChange: true, refetchOnFocus: true }
  );

  const { byTemplateId: appointmentTypesByTemplateId } =
    useScribeTemplateAppointmentTypes(isTemplateTypesEnabled);
  const [generateAsync, { isLoading: isGenerateInProgress }] =
    useGenerateAmbientScribeAsyncMutation();

  // Strict `=== null`, not `== null`: `null` is the backend saying "the built-in prompt generated
  // this", while `undefined` means the field was never projected (the flag-off scribe query omits
  // it). Conflating them would claim built-in provenance for a scribe we know nothing about.
  const cameFromBuiltIn = scribeTemplateId === null;

  // What the picker shows, in the shared select's three-state form: an id, `null` for the built-in
  // prompt, or `undefined` for "we cannot name what produced this" (a soft-deleted template, or
  // one the projection/limit never returned).
  const pickerValue = useMemo((): number | null | undefined => {
    if (selectedTemplateId !== undefined) return selectedTemplateId;
    if (cameFromBuiltIn) return null;
    return scribeTemplateId != null &&
      templates.some((t) => t.id === scribeTemplateId)
      ? scribeTemplateId
      : undefined;
  }, [selectedTemplateId, cameFromBuiltIn, scribeTemplateId, templates]);

  /** The template Generate would use. `null` means the backend's built-in prompt. */
  const effectiveTemplateId = pickerValue ?? null;

  const hasTemplates = templates.length > 0;

  // Type-scoped on purpose: `templateType` is optional, so with no `?type=` filter this list can
  // also hold AudiologyReport templates — and `is_default` is scoped per type, so an audiology
  // default would answer the wrong question.
  const hasDefaultChartNoteTemplate = useMemo(
    () =>
      templates.some(
        (template) => isChartNoteTemplate(template.type) && template.is_default
      ),
    [templates]
  );

  // Offered when the practice has no default ChartNote template to fall back on — and always when
  // this scribe actually came from the built-in prompt, or the user has just chosen it, so the
  // closed control never displays a value with no matching option. Gated on
  // AMBIENT_SCRIBE_TEMPLATE_TYPES so the flag-off DOM and request body stay byte-identical.
  const showBuiltInOption =
    isTemplateTypesEnabled &&
    (!hasDefaultChartNoteTemplate ||
      cameFromBuiltIn ||
      selectedTemplateId === null);

  const isButtonLoading = Boolean(isGeneratingSoap) || isGenerateInProgress;

  // Guard, not a feature: with no `templateType` filter an Audiology Report template can be
  // picked here, and POST /async/ with one writes NOTHING to the scribe — no soap_notes, and
  // is_generating_soap never flips, so this button's spinner would never clear (BE doc §6.2).
  //
  // Audiology summaries DO ship now, but only from an audiology report, which is what supplies
  // the `report_id` the note is keyed to. This surface has no report, so the restriction stands:
  // keep it until an audiology template can be given a report from here too.
  const selectedTemplate = templates.find((t) => t.id === pickerValue);
  const isUnsupportedTemplateType =
    isTemplateTypesEnabled &&
    selectedTemplate != null &&
    !isChartNoteTemplate(selectedTemplate.type);

  const handleGenerate = async () => {
    const payload: { scribe_id: number; ambient_scribe_template_id?: number } =
      {
        scribe_id: scribeId
      };
    // Left off entirely for the built-in prompt — that is how the backend is told to use it.
    if (
      isAmbientScribeCustomTemplatesEnabled &&
      hasTemplates &&
      effectiveTemplateId != null
    ) {
      payload.ambient_scribe_template_id = effectiveTemplateId;
    }
    const result = await generateAsync(payload);
    if ("error" in result) {
      const err = result.error as any;
      if (err?.status === 402 && err?.data?.subscription_state) {
        setGenerateError({
          kind: "subscription",
          error: err.data.error,
          message: err.data.message
        });
      } else {
        setGenerateError({ kind: "generic" });
      }
      return;
    }
    onGenerateStarted();
  };

  return (
    <>
      {/*
        Column wrapper so the hint below lands UNDER the controls. One caller renders this
        inside the soap toolbar's `direction="row"` centre slot (text-tab.component.tsx),
        where a bare sibling would sit beside the picker instead.
      */}
      <Stack>
        {/* flex-end so the button sits on the input baseline once the select has a label. */}
        <Stack
          direction="row"
          spacing={1}
          alignItems={isTemplateTypesEnabled ? "flex-end" : "center"}
          flexWrap="wrap"
        >
          <AmbientScribeTemplateSelect
            value={pickerValue}
            onChange={setSelectedTemplateId}
            disabled={isButtonLoading}
            templates={templates}
            // Makes "no template" pickable, so a practice without a default ChartNote template is
            // not dead-ended by the disabled Generate below.
            builtInOptionLabel={
              showBuiltInOption ? BUILT_IN_CHART_NOTE_TEMPLATE_LABEL : undefined
            }
            showLabel={isTemplateTypesEnabled}
            label="Select Scribe Template"
            showTemplateDetails={isTemplateTypesEnabled}
            appointmentTypesByTemplateId={appointmentTypesByTemplateId}
          />

          <LoadingButton
            variant="contained"
            color="primary"
            onClick={handleGenerate}
            loading={isButtonLoading}
            disabled={
              isButtonLoading ||
              isUnsupportedTemplateType ||
              // Only blocks when there is nothing selected AND no built-in option to select:
              // with the option present, "no template" is a deliberate choice, not an empty form.
              (effectiveTemplateId == null &&
                !showBuiltInOption &&
                isAmbientScribeCustomTemplatesEnabled &&
                hasTemplates)
            }
            size="small"
          >
            {isAmbientScribeCustomTemplatesEnabled && hasTemplates
              ? "Apply"
              : "Generate SOAP Notes"}
          </LoadingButton>
        </Stack>
        {isUnsupportedTemplateType && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
            data-testid="unsupported-template-type-hint"
          >
            Audiology Report templates are used from an audiology report, via
            &quot;Generate Summary with Scribe context&quot;. Pick a Chart Note
            template to generate a scribe here.
          </Typography>
        )}
      </Stack>

      <MuiDialog
        open={generateError !== null}
        onClose={() => setGenerateError(null)}
        sx={{ zIndex: getScribeModalNestedDialogZIndex(theme.zIndex.snackbar) }}
      >
        <DialogTitle>
          {generateError?.kind === "subscription"
            ? "Subscription Required"
            : "Generation Failed"}
        </DialogTitle>
        <DialogContent>
          {generateError?.kind === "subscription" ? (
            <Alert severity="warning">
              <AlertTitle>{generateError.error}</AlertTitle>
              {generateError.message}
            </Alert>
          ) : (
            <Typography>
              Something went wrong while processing your request. Please try
              again in a few moments. If the issue persists, contact Suno
              support for assistance.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateError(null)}>Close</Button>
        </DialogActions>
      </MuiDialog>
    </>
  );
}
